import {
  Button,
  CalendarPicker,
  Loading,
  Panel,
  Select,
  Tab,
  Tabs,
} from "@appkit4/react-components";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useCallback, useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import CustomTooltipIcon from "../../components/customTooltipIcon/customTooltipIcon";
import toast from "../../components/DismissibleToast";
import { GroupNameInput } from "../../components/inputs/GroupNameInput";
import { AuthContext } from "../../services/AuthProvider";
import {
  get_group_companies,
  get_onesource_groups,
} from "../../services/dredger/dredger.service";
import { setGroupData } from "../../services/GroupContext";
import sqlService from "../../services/sqldatabase/sqldatabase.service";
import { setUserGroupRole } from "../../store/slice/usageInfoSlice";
import {
  getDefaultPeriodEnd,
  getMaxPeriodEnd,
  getOriginatingPoaMinStartDateJS,
  processDate,
  processDateJS,
} from "../../utils/dateProcessor";
import { defaultReturnStatus } from "../GroupPOA/GroupPoAUtils";
import "./newGroup.scss";

dayjs.extend(utc);

export const getDefaultPeriod = (
  model_first_period_start,
  model_first_period_end,
) => {
  return {
    id: uuidv4(),
    period_start: model_first_period_start,
    period_end: model_first_period_end,
    return_status: { ...defaultReturnStatus },
    ratio_type: "Fixed Ratio",
    disallowance_reactivation: "",
    group_excess_debt_cap: "",
    unused_interest_allowance: "",
    unused_allowance: "",
  };
};

const NewGroup = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isCreatingGroup, setIsCreatingGroup] = useState(false);

  const [oneSourceGroups, setOneSourceGroups] = useState([]);

  const [selected_group, set_selected_group] = useState(""); // The name of the group that has been selected  on the dropdown

  const [groupName, setGroupName] = useState("");
  const [groupNameError, setGroupNameError] = useState(false);
  const [lastInvalidName, setLastInvalidName] = useState(null);
  const [groupNameIsLastDuplicateName, setGroupNameIsLastDuplicateName] =
    useState(false);
  useEffect(() => {
    setGroupNameIsLastDuplicateName(groupName === lastInvalidName);
  }, [groupName, lastInvalidName]);

  const [group_id, setGroupID] = useState();
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(
    processDateJS(getDefaultPeriodEnd(dayjs.utc()).toDate()),
  );
  const [endDateError, setEndDateError] = useState("");
  const [canSubmit, setCanSubmit] = useState(false);

  const auth = useContext(AuthContext);
  const dispatch = useDispatch();
  const isClientFacing = useSelector((state) => state.usageInfo.isClientFacing);
  const tenantId = useSelector((state) => state.usageInfo.tenantID);

  const navigate = useNavigate();

  const [tabIndex, setTabIndex] = useState(isClientFacing ? 1 : 0);
  const onTabChange = (i, value) => {
    setGroupName("");
    set_selected_group("");
    setGroupID(null);
    setTabIndex(i);
  };

  const isFromOneSource = () => {
    return tabIndex === 0;
  };

  const getStartEnd = () => {
    const start = processDateJS(startDate);
    const end = processDateJS(endDate);

    return { start, end };
  };

  const isEndBeforeStartDate = () => {
    const { start, end } = getStartEnd();

    return end?.isBefore(start);
  };

  const isEndAfterMaxDate = () => {
    const { start, end } = getStartEnd();
    let maximumDate = start?.add(18, "months", "day")?.subtract(1, "day");

    return end?.isAfter(maximumDate);
  };

  useEffect(() => {
    setEndDateError("");

    if (isEndBeforeStartDate()) {
      setEndDateError("End Date is before the Start Date");
    }

    if (isEndAfterMaxDate()) {
      setEndDateError(
        "The difference between Start Date and End Date cannot exceed 18 months",
      );
    }
  }, [startDate, endDate]);

  const isEndDateValid = useCallback(() => {
    if (isEndBeforeStartDate()) {
      return false;
    }

    if (isEndAfterMaxDate()) {
      return false;
    }

    return true;
  }, [startDate, endDate]);

  const [lookup, setLookup] = useState([]);

  // Function to handle change in start date
  const handleStartDateChange = (value) => {
    const newStartDate = processDate(value);
    setStartDate(newStartDate);
    // Calculate end date by adding 1 year to start date
    const newEndDate = new Date(newStartDate);
    newEndDate?.setFullYear(newEndDate?.getFullYear() + 1);
    newEndDate?.setDate(newEndDate?.getDate() - 1);
    setEndDate(newEndDate);
  };

  useEffect(() => {
    async function fetchData() {
      setIsLoading(true);
      try {
        const userEmail = auth?.getEmail();
        const response = await get_onesource_groups(userEmail);
        let formattedGroups = [];
        const initialLookup = {};
        if (response?.status === 200) {
          formattedGroups = response?.data?.map((val) => {
            return {
              value: val?.GroupName,
              label: val?.GroupName,
            };
          });

          for (const entry of response?.data) {
            initialLookup[entry?.GroupName] = entry?.GroupID;
          }
        }
        setLookup(initialLookup);
        //This is to remove duplicates
        setOneSourceGroups([
          ...new Set(formattedGroups.map((group) => group.value)),
        ]); //formattedGroups)
        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        setOneSourceGroups([{ label: "Failed to load from OneSource" }]);
        setIsLoading(false);
      }
    }

    if (!isClientFacing) {
      fetchData();
    }
  }, []);

  useEffect(() => {
    // TODO is last
    if (!isEndDateValid()) {
      setCanSubmit(false);
      return;
    }

    if (startDate < getOriginatingPoaMinStartDateJS()) {
      setCanSubmit(false);
      return;
    }

    setCanSubmit(!groupNameError && !groupNameIsLastDuplicateName);
  }, [groupNameError, endDate, startDate, groupNameIsLastDuplicateName]);

  useEffect(() => {
    if (!groupName) {
      return;
    }

    const groupID = lookup[groupName];
    if (groupID) {
      setGroupID(groupID);
    }
  }, [groupName]);

  const submit = async () => {
    setIsCreatingGroup(true);
    let result;
    try {
      result = await sqlService?.getIsDuplicateGroupName(tenantId, groupName);

      const isGroupNameValid =
        result?.status === 200 && result?.data?.isDuplicate === false;

      if (!isGroupNameValid) {
        if (result?.data?.isDuplicate) {
          toast.error(
            `The group name already exists in the CIR app. Please enter a different name to create a new group, or request access to the group from the homepage.`,
          );

          setLastInvalidName(groupName);
        } else {
          toast.error(`Failed to validate the group name. Please try again.`);
        }

        setIsCreatingGroup(false);
        return;
      }
    } catch (error) {
      toast.error(`Error: ${error?.message}`);
      setIsCreatingGroup(false);
      return;
    }

    const userEmail = auth?.getEmail();
    if (!userEmail) {
      toast.error(`User profile could not be found, failed to create group`);
      setIsCreatingGroup(false);
      return;
    }
    let data = {
      group_name: groupName,
      group_id: group_id ? group_id : null,
      model_first_period_start: startDate,
      model_first_period_end: endDate,
      periods_of_account: [getDefaultPeriod(startDate, endDate)],
    };

    // If group_id is set here, this means data was pulled from OneSource.
    // Let's fetch the data neccessary here
    if (group_id) {
      try {
        const companies = await get_group_companies(
          Number(group_id),
          userEmail,
        );
        const companyIDs = companies.map(({ company_ID }) => company_ID);
        const onlyPeriod = data.periods_of_account[0];

        const periods_of_account = [
          { ...onlyPeriod, ukCompanyIDs: companyIDs },
        ];
        data = { ...data, periods_of_account, companies };
      } catch (err) {
        toast.error(
          `Failed to fetch OneSource companies for selected group, please try again!`,
        );
        console.error(err);
        setIsCreatingGroup(false);
        return;
      }
    }

    result = sqlService?.postNewGroup(tenantId, data);
    const onSuccess = (response) => {
      const { groupID } = response?.data;
      setGroupData({
        ...data,
        groupID: groupID,
      });
      dispatch(setUserGroupRole("Supervisor"));
      setIsCreatingGroup(false);
      const lockGroup = sqlService.postLockGroup(tenantId, groupID, true);
      toast.promise(lockGroup, {
        loading: "Locking Group...",
        success: (response) => {
          if (response.status === 200) {
            navigate("/app/userAccess");
            return `Group Locked`;
          } else {
            throw new Error(
              `Error Locking Group: ${response.err ?? response.error ?? ""}`,
            );
          }
        },
        error: (err) =>
          `Error locking group: ${err.error ?? err.message ?? ""}`,
      });
      setIsCreatingGroup(false);
    };
    const onError = () => {
      setIsCreatingGroup(false);
    };
    toast.save(result, onSuccess, onError);
  };

  if (isLoading) {
    return (
      <Loading
        loadingType="linear"
        indeterminate={true}
        compact={false}
        className="page-loader"
      />
    );
  }

  return (
    <div className="ap-container">
      <Panel title="Group setup" className="mt-4 mb-4">
        <p>
          The legislation governing Financial statements and periods of account
          is located at s479 - s489.
        </p>
        <p>
          The CIR period generally follows the period for which consolidated
          financial statements are drawn up for the group or the period for
          which consolidated financial statements are treated as having been
          drawn up. Where the consolidated financial statements are not
          considered to be drawn up in an appropriate manner, or not drawn up at
          all, s481-485 provide specific rules to determine the period of
          account and deem appropriate financial statements to have been
          prepared. These are subject to an election under s486.
        </p>
        <p>
          Note that under s487 it is not possible for a CIR period of account to
          exceed 18 months in duration - where this is the case, specific rules
          apply to ignore the actual financial statements and determine deemed
          financial statements for CIR purposes.
        </p>
      </Panel>
      <Panel title="Add a new group">
        <p>
          {isClientFacing
            ? "Enter the name of the new group in the box below."
            : "When creating a new group, you can choose a group that exists in OneSource or manually create your own."}
        </p>

        {!isClientFacing && (
          <Tabs
            stretched
            type="filled"
            activeIndex={tabIndex}
            onTabChange={onTabChange}
          >
            <Tab label="From OneSource" value="From OneSource"></Tab>
            <Tab label="Manual Entry" value="Manual Entry"></Tab>
          </Tabs>
        )}

        {isFromOneSource() ? (
          <>
            <i className="Appkit4-icon icon-circle-warning-outline"></i>
            <span>
              {" "}
              Please contact{" "}
              <a
                href="mailto:uk_cir_app_support@pwc.com?subject=New Group Creation"
                target="_blank"
                rel="noreferrer"
              >
                uk_cir_app_support@pwc.com
              </a>{" "}
              if you cannot find a group that is in OneSource.
            </span>
            <div className="row mt-1">
              <Select
                searchable
                data={oneSourceGroups}
                placeholder="Select from an existing group from OneSource"
                searchPlaceholder="Start typing the name of the group..."
                searchType={"secondary"}
                onSelect={(value) => {
                  set_selected_group(value);
                  setGroupName(value);
                }}
              />

              {selected_group && (
                <GroupNameInput
                  style={{ paddingTop: 10 }}
                  value={groupName}
                  title="Edit Group Name"
                  onChange={setGroupName}
                  setError={setGroupNameError}
                  error={groupNameError || groupNameIsLastDuplicateName}
                  errorNode={
                    groupNameIsLastDuplicateName
                      ? "This name is already associated with a group"
                      : ""
                  }
                />
              )}
            </div>
          </>
        ) : (
          <>
            <div className="row">
              <GroupNameInput
                title="Group Name"
                className="col"
                value={groupName}
                onChange={setGroupName}
                setError={setGroupNameError}
                error={groupNameError || groupNameIsLastDuplicateName}
                errorNode={
                  groupNameIsLastDuplicateName
                    ? "This name is already associated with a group"
                    : ""
                }
              />
            </div>
          </>
        )}
      </Panel>

      <Panel title="First Period of Account" style={{ marginTop: 20 }}>
        <div>
          <p>
            Please enter the first CIR return Period of Account (PoA) for which
            this tool is used:{" "}
          </p>
          <div className="row mb-3 w-full">
            <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
              <CalendarPicker
                fieldTitle="PoA Start Date"
                placeholder="dd/mm/yyyy"
                format="DD/MM/YYYY"
                value={startDate}
                minDate={getOriginatingPoaMinStartDateJS()}
                onChange={handleStartDateChange}
              />
              <CustomTooltipIcon
                size="big"
                tooltipText={[
                  "This is the first period of account for which the CIR Analyser is being used and will not necessarily be the first CIR period of the group.",
                  "The CIR period generally follows the period for which consolidated financial statements are drawn up for the group or the period for which consolidated financial statements are treated as having been drawn up. Refer to s479 - s489 TIOPA 2010 for further details.",
                ]}
              />
            </div>
          </div>

          <div className="row">
            <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
              <CalendarPicker
                fieldTitle="PoA End Date"
                placeholder="dd/mm/yyyy"
                value={endDate}
                format="DD/MM/YYYY"
                useCustomValidation
                error={endDateError !== ""}
                customErrorNode={endDateError}
                onChange={(newDate) => {
                  setEndDate(processDate(newDate));
                }}
                minDate={startDate}
                maxDate={getMaxPeriodEnd(startDate)}
              />
              <CustomTooltipIcon
                size="big"
                tooltipText={[
                  "This is the first period of account for which the CIR Analyser is being used and will not necessarily be the first CIR period of the group.",
                  "Under s487 TIOPA 2010 it is not possible for a CIR period of account to exceed 18 months in duration. Where this is the case, specific rules apply to ignore the actual financial statements and determine deemed financial statements for CIR purposes. Refer to s479 - s489 TIOPA 2010 for further details.",
                ]}
              />
            </div>
          </div>
        </div>
      </Panel>

      <div className="d-flex justify-content-end ap-container mt-4">
        <Button
          disabled={!canSubmit}
          onClick={submit}
          loading={isCreatingGroup}
        >
          Create group
        </Button>
      </div>
    </div>
  );
};

export default NewGroup;
